import { Component, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { Event } from '../../../../../../models/event';
import { EventService } from '../../../../../../services/event/event.service';
import { AccountService } from '../../../../../../services/account/account.service';
import { ScheduledBooking } from '../../../../../../models/scheduled-booking';
import { ScheduledBookingService } from '../../../../../../services/scheduled-booking/scheduled-booking.service';
import { ScheduledEvent } from '../../../../../../models/scheduled-event';
import { eventFullDueToSecondaryCapacity } from '../../../../../../utils-models';
import { ScheduledEventService } from '../../../../../../services/scheduled-event/scheduled-event.service';
import { timeStringFromISO8601 } from '../../../../../../utils';

@Component({
  selector: 'app-rebook-modal',
  templateUrl: './rebook-modal.component.html',
  styleUrls: ['./rebook-modal.component.css']
})
export class RebookModalComponent implements OnInit {

  @Input() bookingId: number;
  @Input() savedCallback: () => undefined;

  public booking: ScheduledBooking;

  public selectedDate: string;
  public scheduledEventsForDate: ScheduledEvent[] = [];
  public selectedEvent: Event = null;

  public rebookingEmailText = '';

  public loadingBooking = false;
  public loadingEventsForDate = false;
  public loadingSelectedEvent = false;
  public loadingSubmit = false;

  public errorMessage = '';
  public serverError: number = null;

  // The number of loadEventsForDate calls. Used to ensure that only the most
  // recent result is displayed, in the event of concurrent requests (due to,
  // e.g., rapid pagination).
  private loadEventsForDateRequestCount = 0;

  public eventFullDueToSecondaryCapacity = eventFullDueToSecondaryCapacity;
  public timeStringFromISO8601 = timeStringFromISO8601;

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal,
    private eventService: EventService,
    private bookingService: ScheduledBookingService,
    private scheduledEventService: ScheduledEventService
  ) {}

  ngOnInit() {
    this.loadBooking();
  }

  public loadBooking() {
    this.loadingBooking = true;

    this.bookingService
      .getScheduledBooking(this.bookingId)
      .then(booking => {
        this.booking = booking;
        this.selectedDate = booking.event_parts[0].event_date;
        this.loadEventsForDate();
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingBooking = false; });
  }

  public loadEventsForDate() {
    this.loadingEventsForDate = true;

    // Increment the request count and save a local copy.
    this.loadEventsForDateRequestCount++;
    const thisRequestCount = this.loadEventsForDateRequestCount;

    this.scheduledEventService
      .getScheduledEvents(
        this.selectedDate,
        this.booking.event_type_id,
        false
      )
      .then(scheduledEvents => {
        // Only update the events list if there has not been a more recent
        // request initiated.
        if (thisRequestCount !== this.loadEventsForDateRequestCount) {
          return;
        }

        this.scheduledEventsForDate = scheduledEvents;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.loadEventsForDateRequestCount) {
          this.loadingEventsForDate = false;
        }
      });
  }

  public loadSelectedEvent(selectedEventId: number) {
    this.loadingSelectedEvent = true;

    this.eventService
      .getEvent(selectedEventId)
      .then(event => {
        this.selectedEvent = event;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingSelectedEvent = false;
      });
  }

  public isLoading() {
    return (
      this.loadingBooking ||
      this.loadingEventsForDate ||
      this.loadingSelectedEvent ||
      this.loadingSubmit
    );
  }

  public handleSubmit() {
    this.loadingSubmit = true;

    this.bookingService
      .rebook(this.bookingId, this.selectedEvent.id, this.rebookingEmailText)
      .then(() => {
        this.savedCallback();
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to rebook this reservation.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }
}
